import axios from 'axios';
import * as R from 'ramda';

import { defineSplitString } from '../utils';
import { AdyenPaymentResponse } from '../types';

const baseURL = 'https://localhost.erply.com:10108/api/v1';

// Host reference number needs to be 6 characters long and unique in any request
let hostReferenceNumber;

const MANAGE_SETTINGS = {
  url: `${baseURL}/manage`,
  method: 'POST',
  headers: {
    Accept: '*',
    'Content-Type': 'application/json',
  },
} as const;

const PAYMENT_SETTINGS = {
  url: `${baseURL}/payment`,
  method: 'POST',
  headers: {
    Accept: '*',
    'Content-Type': 'application/json',
  },
} as const;

const TEST_CONNECTION_SETTINGS = {
  url: `${baseURL}/testConnection`,
  method: 'GET',
  headers: {
    Accept: '*',
    'Content-Type': 'application/json',
  },
} as const;

type Response = AdyenPaymentResponse | { statusMessage: string };

export async function requestPayment(
  paymentDataProp,
  config,
  shopperTransactionID: string | null = null,
  splitsConf,
  employeeID,
  additionalData,
): Promise<Response> {
  const { currencyCode, adyenRecon, customerEmail } = config;

  const shopperID = Math.floor(Date.now() / 1000).toString();
  hostReferenceNumber = shopperID.slice(-6);

  const transactionISOTime = new Date().toISOString();
  const dateDataForReferenceNumber = transactionISOTime.split('T');

  const paymentData = {
    CardAcquisitionReference: {
      TransactionID: shopperTransactionID,
      TimeStamp: transactionISOTime,
    },
  };

  type Body = {
    transactionType: string;
    amount: string;
    employeeID: number;
    invoiceNumber: string;
    softwareName: string;
    softwareVersion: string;
    currency: string;
    referenceNumber: string;
    hostReferenceNumber: string;
    // AFAICT is ignored by MS
    SaleToPOIRequest?: {
      PaymentRequest: {
        SaleData?: {
          SaleToAcquirerData: string;
          TokenRequestedType?: 'Customer' | string;
        };
        PaymentData?: {
          CardAcquisitionReference?: {
            TransactionID?: null;
            TimeStamp?: string;
          };
        };
      };
    };
  };

  let requestBody: Body = {
    transactionType: 'SALE',
    amount: String(paymentDataProp.amount),
    employeeID,
    invoiceNumber: String(paymentDataProp.id),
    softwareName: 'Erply Berlin POS',
    softwareVersion: '3.32.3',
    currency: currencyCode,
    referenceNumber: `${dateDataForReferenceNumber[0]}-${employeeID}-${dateDataForReferenceNumber[1]}`,
    hostReferenceNumber,
  };

  if (splitsConf && splitsConf.length) {
    // Adds split string
    requestBody = R.assocPath(
      ['SaleToPOIRequest', 'PaymentRequest', 'SaleData', 'SaleToAcquirerData'],
      defineSplitString(splitsConf, paymentDataProp, currencyCode),
    )(requestBody);
  }

  if (adyenRecon) {
    if (customerEmail && customerEmail !== ' ' && customerEmail !== '') {
      requestBody = R.assocPath(
        ['SaleToPOIRequest', 'PaymentRequest', 'SaleData'],
        {
          SaleToAcquirerData: `shopperEmail=${customerEmail}&shopperReference=${shopperID}&recurringContract=RECURRING`,
          TokenRequestedType: 'Customer',
        },
      )(requestBody);
    }

    if (shopperTransactionID) {
      requestBody = R.assocPath(
        ['SaleToPOIRequest', 'PaymentRequest', 'PaymentData'],
        paymentData,
      )(requestBody);
    }
  }
  if (additionalData) {
    requestBody = R.assoc('additionalData', additionalData)(requestBody);
  }

  const data = requestBody;

  return axios({ ...PAYMENT_SETTINGS, data })
    .then(response => response.data.records[0])
    .catch(error => {
      console.error({ error });
      return {
        statusMessage:
          'Error while performing payment transaction, please try again',
      };
    });
}

export const cancelPayment = async (employeeID, isPaymentOrReturn) => {
  const abortRequest = {
    manageType: 'cancelTransaction',
    data: `{"employeeID": "${employeeID}", "serviceID": "${hostReferenceNumber}", "messageCategory":"${isPaymentOrReturn}"}`,
  };

  return axios({ ...MANAGE_SETTINGS, data: abortRequest }).then(
    response => response.data.records[0],
  );
};

export const checkTransactionStatus = async employeeID => {
  const data = {
    manageType: 'status',
    data: `{"employeeID": "${employeeID}", "serviceID":"${hostReferenceNumber}", "messageCategory":"Payment"}`,
  };

  return axios({ ...MANAGE_SETTINGS, data }).then(
    response => response.data.records[0],
  );
};

export const voidRequest = async paymentData => {
  const referenceNumber =
    paymentData.attributes?.refNo ??
    paymentData.original?.referenceNumber ??
    '';
  const data = {
    transactionType: 'VOID',
    referenceNumber,
  };

  return axios({ ...PAYMENT_SETTINGS, data }).then(
    response => response.data.records[0],
  );
};

export const refundRequest = async (paymentData, currencyCode, employeeID) => {
  const { sum: origSum } = paymentData.original ?? {};
  const referenceNumber =
    paymentData.attributes?.refNo ??
    paymentData.original?.referenceNumber ??
    '';
  const originalTip = paymentData.attributes?.originalTip;
  function getOrigAmount(): string {
    if (origSum) {
      if (originalTip) {
        return (Number(origSum) + originalTip).toFixed(2);
      }
      return origSum;
    }
    return '';
  }
  const amount = Math.abs(Number(paymentData.amount));
  hostReferenceNumber = Math.floor(Date.now() / 1000)
    .toString()
    .slice(-6);

  const data = {
    transactionType: 'REFUND',
    employeeID,
    amount: String(amount.toFixed(2)),
    referenceNumber,
    origDateTime: new Date().toISOString(),
    currency: currencyCode,
    origAmount: getOrigAmount(),
    hostReferenceNumber,
  };

  return axios({ ...PAYMENT_SETTINGS, data }).then(
    response => response.data.records[0],
  );
};

export const terminalDiagnosis = async () => {
  return axios({ ...TEST_CONNECTION_SETTINGS }).then(
    response => response.data.records[0],
  );
};

export const getCustomerData = async (cardPayment, config) => {
  const { terminalId } = config;
  const newUniqueIdVal = Math.floor(Date.now() / 1000).toString();
  const requestBody = {
    SaleToPOIRequest: {
      MessageHeader: {
        ProtocolVersion: '3.0',
        MessageClass: 'Service',
        MessageCategory: 'CardAcquisition',
        MessageType: 'Request',
        SaleID: newUniqueIdVal,
        ServiceID: newUniqueIdVal.toString().slice(-6),
        POIID: terminalId,
      },
      CardAcquisitionRequest: {
        SaleData: {
          SaleTransactionID: {
            TransactionID: newUniqueIdVal.toString().slice(-4),
            TimeStamp: new Date().toISOString(),
          },
          TokenRequestedType: 'Customer',
        },
        CardAcquisitionTransaction: {
          TotalAmount: Number(cardPayment.amount),
        },
      },
    },
  };

  const decryptedResponse = await axios({
    ...PAYMENT_SETTINGS,
    data: requestBody,
  });
  return decryptedResponse;
};
